import { getTrackingLink } from "@redotech/redo-fulfillment-carrier-common/carriers-and-services";
import { knownScacToLabel } from "@redotech/redo-fulfillment-carrier-common/freight";
import { FulfillmentGroupData } from "./fulfillment-group";
import { summaryPackages } from "./shipment-summary";

/** The only fields tracking is derived from. */
export type FulfillmentGroupTrackingSource = Pick<
  FulfillmentGroupData,
  "shipmentSummary" | "fulfillmentInfo"
>;

export interface FulfillmentGroupTracking {
  trackingNumber: string;
  carrier: string | null | undefined;
  /**
   * Empty when we can't build a link: carriers outside the code-derived set
   * (UPS, FedEx, USPS, DHL, OnTrac, ...) are tracked through a tracker-hosted
   * `publicUrl`, which a manually-fulfilled group never has.
   */
  link: string;
}

/**
 * Every tracking number on a group, in source precedence order, paired with the
 * carrier tracking link for each.
 *
 * A group carries tracking in one of three shapes: a purchased label
 * (the packages across `shipmentSummary.carrierShipments`), a manual fulfillment
 * (`fulfillmentInfo`, written from `manualFulfillmentInfo` — a
 * mark-as-fulfilled with a tracking code never populates a shipment), or a
 * freight booking (`shipmentSummary.freight`).
 */
export function getFulfillmentGroupTracking(
  row: FulfillmentGroupTrackingSource,
): FulfillmentGroupTracking[] {
  const tracking: FulfillmentGroupTracking[] = [];
  const packages = summaryPackages(row.shipmentSummary);

  if (packages.some((shipmentPackage) => shipmentPackage.trackingCode)) {
    for (const shipmentPackage of packages) {
      if (shipmentPackage.trackingCode) {
        tracking.push({
          trackingNumber: shipmentPackage.trackingCode,
          carrier: shipmentPackage.trackingCarrier,
          link: shipmentPackage.trackingUrl ?? "",
        });
      }
    }
  } else if (row.fulfillmentInfo?.trackingCode) {
    tracking.push({
      trackingNumber: row.fulfillmentInfo.trackingCode,
      carrier: row.fulfillmentInfo.carrier,
      link: getTrackingLink(
        row.fulfillmentInfo.carrier,
        row.fulfillmentInfo.trackingCode,
        "",
        row.fulfillmentInfo.service || undefined,
      ),
    });
  } else {
    const freight = row.shipmentSummary?.freight;
    if (freight) {
      tracking.push({
        trackingNumber: freight.trackingCode,
        carrier: knownScacToLabel[freight.scac],
        link: freight.trackingUrl,
      });
    }
  }

  return dedupeByTrackingNumber(tracking);
}

export function getTrackingNumbers(
  row: FulfillmentGroupTrackingSource,
): string[] {
  return getFulfillmentGroupTracking(row).map(
    ({ trackingNumber }) => trackingNumber,
  );
}

function dedupeByTrackingNumber(
  tracking: FulfillmentGroupTracking[],
): FulfillmentGroupTracking[] {
  const byTrackingNumber = new Map<string, FulfillmentGroupTracking>();
  for (const entry of tracking) {
    if (!byTrackingNumber.has(entry.trackingNumber)) {
      byTrackingNumber.set(entry.trackingNumber, entry);
    }
  }
  return [...byTrackingNumber.values()];
}
